import React, { useCallback, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../../../shared/components/button';
import { SegmentedControl } from '../../../../../shared/components/segmented-control';
import TextInput from '../../../../../shared/components/text-input';
import Textarea from '../../../../../shared/components/textarea';
import { isMobileUa } from '../../../../../shared/features/uatraits/selectors';
import { isYandexSsoUserSelector } from '../../../../../shared/features/user/selectors';
import { i18n } from '../../../../../shared/helpers/i18n';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { countMetrika, countMetrikaObj } from '../../../../../shared/helpers/metrika';
import { useOnMount } from '../../../../../shared/hooks/useOnMount';
import { AccessLevel } from '../../../../../types';
import { canCreateOrganizationRestrictedConferenceSelector } from '../../../../call/selectors';
import { planBroadcast, PlanBroadcastAction } from '../../../../main/features/create-call/slice';
import { startBroadcastFromFormAction, StartBroadcastFromFormActionInterface } from '../slice';
import classes from './index.css';

const logger = createLogger('create-broadcast-form');

const countCreateStream = (isPlanning: boolean, caption: string, description: string) => {
    const isNameFilled = caption ? 'true' : 'false';
    const isDescrFilled = description ? 'true' : 'false';

    countMetrikaObj({
        main_screen: {
            [isPlanning ? 'plan_stream' : 'new_stream']: {
                name_filled: isNameFilled,
                descr_filled: isDescrFilled,
            },
        },
    });

    logger.event('Broadcast created via form', {
        additional: {
            method: 'countCreateStream',
            isPlanning,
            isNameFilled,
            isDescrFilled,
        },
    });
};

const CreateBroadcastForm = () => {
    const dispatch = useDispatch();
    const isMobile = useSelector(isMobileUa);
    const isYandexSsoUser = useSelector(isYandexSsoUserSelector);
    const isPrivateConfEnabled = useSelector(canCreateOrganizationRestrictedConferenceSelector);

    const [caption, setCaption] = useState('');
    const [description, setDescription] = useState('');
    const [accessLevel, setAccessLevel] = React.useState(AccessLevel.PUBLIC);

    const captionInputRef = useRef<HTMLInputElement>(null);

    useOnMount(() => {
        countMetrika('main_screen', 'new_stream', 'form_showed');
        logger.event('Broadcast creation form showed', {
            additional: {
                method: 'useOnMount',
            },
        });
    });

    const onCaptionChange = useCallback((event) => {
        setCaption(event.target.value);
    }, []);

    const onDescriptionChange = useCallback((event) => {
        setDescription(event.target.value);
    }, []);

    const onAccessLevelChange = React.useCallback((value) => {
        setAccessLevel(value);
    }, []);

    const onSubmitForm = useCallback(
        (event) => {
            event.preventDefault();

            const payload: StartBroadcastFromFormActionInterface = { caption, description };

            if (isPrivateConfEnabled) {
                payload.accessLevel = accessLevel;
            }

            dispatch(startBroadcastFromFormAction(payload));
            countCreateStream(false, caption, description);
        },
        [dispatch, caption, description, accessLevel, isPrivateConfEnabled]
    );

    const onPlanButtonClick = useCallback(() => {
        const payload: PlanBroadcastAction = { caption, description };

        if (isPrivateConfEnabled) {
            payload.accessLevel = accessLevel;
        }

        dispatch(planBroadcast(payload));
        countCreateStream(true, caption, description);
    }, [dispatch, caption, description, accessLevel, isPrivateConfEnabled]);

    const onStartButtonClick = useCallback(() => {
        const payload: StartBroadcastFromFormActionInterface = { caption, description };

        if (isPrivateConfEnabled) {
            payload.accessLevel = accessLevel;
        }

        dispatch(startBroadcastFromFormAction(payload));
        countCreateStream(false, caption, description);
    }, [caption, description, dispatch, accessLevel, isPrivateConfEnabled]);

    return (
        <form className={classes.CreateBroadcastForm} onSubmit={onSubmitForm}>
            <h1 className={classes.CreateBroadcastForm__Title}>{i18n('create-broadcast', 'form-title')}</h1>
            <TextInput
                inputWrapperRef={captionInputRef}
                className={classes.CreateBroadcastForm__Input}
                size="lg"
                onChange={onCaptionChange}
                value={caption}
                placeholder={i18n('create-broadcast', 'caption-placeholder-v2')}
            />
            <Textarea
                className={classes.CreateBroadcastForm__Textarea}
                size="lg"
                autoResize
                onChange={onDescriptionChange}
                value={description}
                placeholder={i18n('create-broadcast', 'description-placeholder')}
            />
            {isPrivateConfEnabled ? (
                <SegmentedControl
                    className={classes.CreateBroadcastForm__AccessRadioButton}
                    size="md"
                    value={accessLevel}
                    onChange={onAccessLevelChange}
                    container="fixed"
                    options={[
                        {
                            value: AccessLevel.PUBLIC,
                            children: i18n('create-broadcast', 'access-level-public'),
                        },
                        {
                            value: AccessLevel.ORGANIZATION,
                            children: i18n('create-broadcast', 'access-level-organization'),
                        },
                    ]}
                />
            ) : null}
            <div className={classes.CreateBroadcastForm__Buttons}>
                <Button
                    className={classes.CreateBroadcastForm__StartButton}
                    view="neutral"
                    size="md"
                    onClick={onStartButtonClick}
                >
                    {i18n('create-broadcast', 'start-button-short')}
                </Button>
                {!isMobile && (
                    <Button
                        className={classes.CreateBroadcastForm__PlanButton}
                        view="brand"
                        size="md"
                        onClick={onPlanButtonClick}
                        isDisabled={isYandexSsoUser}
                    >
                        {i18n('create-call', 'plan-call-text')}
                    </Button>
                )}
            </div>
        </form>
    );
};

export default CreateBroadcastForm;
